import { Field, Textarea } from "@headlessui/react";
import clsx from "clsx";

/**
 * Props interface for the TextareaField component.
 */
interface TextareaFieldProps extends React.ComponentProps<typeof Textarea> {
  /** Optional error message to display above the textarea */
  error?: string | undefined;

  /** Optional ref to forward to the textarea element */
  ref?: React.Ref<HTMLElement>;

  /** The name of the textarea field */
  name: string; // added by register method from react-hook-form

  /** Optional placeholder text for the textarea */
  placeholder?: string;

  /** Optional max length for the textarea */
  maxLength?: number;

  value?: string;
}

/**
 * A reusable textarea field component that provides consistent textarea styling and behavior.
 *
 * Features:
 * - Standard textarea field styling
 * - Error state handling with visual feedback
 * - Proper accessibility with Headless UI components
 * - Responsive design with Tailwind CSS
 * - Designed to work inside FormContainer
 *
 * @example
 * ```tsx
 * <TextareaField
 *   placeholder="Enter your bio"
 *   error="Please enter a valid bio"
 *   onChange={(e) => setBio(e.target.value)}
 * />
 * ```
 */
export default function TextareaField({ error, ref, className, ...props }: TextareaFieldProps) {
  return (
    <Field className="relative">
      {/* Error message with proper spacing */}
      {error && (
        <div className="mb-2 px-3 py-2 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-600 font-medium" data-testid="field-error">
            {error}
          </p>
        </div>
      )}
      <div className="relative">
        <Textarea
          {...props}
          {...(ref && { ref })}
          className={clsx(
            "w-full px-3 py-2 border-2 rounded-lg bg-white text-gray-600 font-mono focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent text-base resize-none",
            {
              "border-red-500": error,
              "border-gray-300": !error,
            },
            className
          )}
          data-testid="textarea-field"
        />
      </div>
    </Field>
  );
}
